import AdoptionTrackerAPI from '../../services/AdoptionTrackerAPI'

/**
 * Module-scoped (not Vuex state — this is plumbing, not UI-relevant data) guard
 * against overlapping pollBackfill() loops. Without it, changing the window
 * twice in a row (e.g. 7 -> 14 before the first poll finishes) starts a second
 * setInterval while the first is still ticking; whichever one's finish() runs
 * last wins, so a slow/stale poll can stomp a newer one's result — or just
 * leave two intervals both hammering the status endpoint. Each pollBackfill()
 * call bumps the generation and only acts on ticks from its own generation, so
 * a poll that's been superseded silently retires instead of fighting the
 * current one for backfillInProgress.
 */
let activePollTimer = null
let pollGeneration = 0

/**
 * State for the per-location dashboard (resources/views/adoption/dashboard
 * .blade.php's Vue replacement). One location per page load — no need to key
 * state by locationId since the encrypted SSO blob already scopes every
 * request server-side.
 */
export default {
  namespaced: true,

  state: () => ({
    loading: true,
    error: null,
    configured: null,
    name: null,
    scanning: false,
    windowDays: 14,
    backfillInProgress: false,
    installedAt: null,
    lastScanAt: null,
    lastScanError: null,
    overallScore: null,
    periodStart: null,
    periodEnd: null,
    metrics: {},
    history: [],
    thresholds: {},
    baselineWindowDays: 14,
  }),

  getters: {
    isEmpty: (state) => state.overallScore === null && Object.keys(state.metrics).length === 0,
  },

  mutations: {
    SET_LOADING(state, value) { state.loading = value },
    SET_ERROR(state, value) { state.error = value },

    SET_DASHBOARD_DATA(state, data) {
      state.configured = data.configured
      state.name = data.name ?? null
      state.scanning = data.scanning ?? false
      state.windowDays = data.window_days ?? state.windowDays
      state.backfillInProgress = data.backfill_in_progress ?? false
      state.installedAt = data.installed_at ?? null
      state.lastScanAt = data.last_scan_at ?? null
      state.lastScanError = data.last_scan_error ?? null
      state.overallScore = data.overall_score ?? null
      state.periodStart = data.period_start ?? null
      state.periodEnd = data.period_end ?? null
      state.metrics = data.metrics ?? {}
    },

    SET_HISTORY(state, timeline) { state.history = timeline },
    SET_THRESHOLDS(state, payload) {
      state.thresholds = payload.thresholds ?? {}
      state.windowDays = payload.window_days ?? state.windowDays
      state.baselineWindowDays = payload.baseline_window_days ?? state.baselineWindowDays
    },
    SET_BACKFILL_IN_PROGRESS(state, value) { state.backfillInProgress = value },
  },

  actions: {
    async fetchDashboard({ commit }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const data = await AdoptionTrackerAPI.getDashboardData()
        commit('SET_DASHBOARD_DATA', data)
        return data
      } catch (e) {
        commit('SET_ERROR', 'Failed to load. Please reload the page.')
        throw e
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchHistory({ commit }, days = 90) {
      const data = await AdoptionTrackerAPI.getHistory(days)
      commit('SET_HISTORY', data.timeline || [])
      return data
    },

    async fetchThresholds({ commit }) {
      const data = await AdoptionTrackerAPI.getThresholds()
      commit('SET_THRESHOLDS', data)
      return data
    },

    async saveThresholds({ commit, dispatch }, thresholds) {
      const data = await AdoptionTrackerAPI.updateThresholds(thresholds)
      commit('SET_BACKFILL_IN_PROGRESS', true)
      await dispatch('pollBackfill')
      return data
    },

    async changeWindow({ commit, dispatch }, windowDays) {
      await AdoptionTrackerAPI.updateWindow(windowDays)
      commit('SET_BACKFILL_IN_PROGRESS', true)
      await dispatch('pollBackfill')
    },

    /** Polls until the window/threshold backfill finishes, then refreshes the dashboard.
     * Mirrors triggerScan()'s safety net: a capped attempt count and a try/catch around
     * the poll itself, so a slow backfill (queued behind other jobs) or a transient
     * network error can never leave backfillInProgress stuck true forever — which would
     * otherwise keep the window dropdown disabled and its loader spinning indefinitely.
     * Cancels any poll already in flight first — see the module-scoped comment above. */
    pollBackfill({ commit, dispatch }) {
      if (activePollTimer) clearInterval(activePollTimer)
      const myGeneration = ++pollGeneration

      return new Promise((resolve) => {
        const maxAttempts = 80 // ~2min safety cap — backfills can run longer than a scan
        let attempts = 0
        const finish = async () => {
          clearInterval(timer)
          if (myGeneration !== pollGeneration) {
            // A newer poll has since taken over — it owns backfillInProgress now.
            resolve(null)
            return
          }
          activePollTimer = null
          commit('SET_BACKFILL_IN_PROGRESS', false)
          const data = await dispatch('fetchDashboard').catch(() => null)
          resolve(data)
        }
        const timer = setInterval(async () => {
          if (myGeneration !== pollGeneration) {
            // Superseded by a newer pollBackfill() call — retire quietly.
            await finish()
            return
          }
          attempts += 1
          try {
            const status = await AdoptionTrackerAPI.getWindowBackfillStatus()
            if (!status.in_progress || attempts >= maxAttempts) {
              await finish()
            }
          } catch (e) {
            await finish()
          }
        }, 1500)
        activePollTimer = timer
      })
    },

    /** Triggers a scan, then polls the dashboard endpoint itself (it already
     * carries `scanning` alongside the metrics) until scanning clears. */
    triggerScan({ commit, dispatch }) {
      return new Promise((resolve, reject) => {
        AdoptionTrackerAPI.triggerScan().catch(reject)

        const maxAttempts = 40 // ~60s safety cap
        let attempts = 0
        const timer = setInterval(async () => {
          attempts += 1
          try {
            const data = await AdoptionTrackerAPI.getDashboardData()
            if (!data.scanning || attempts >= maxAttempts) {
              clearInterval(timer)
              commit('SET_DASHBOARD_DATA', data)
              resolve(data)
            }
          } catch (e) {
            clearInterval(timer)
            reject(e)
          }
        }, 1500)
      })
    },
  },
}
